import React, { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { translate } from '../../../base/i18n/functions';
import { IconPlus } from '../../../base/icons';
import Icon from '../../../base/icons/components/Icon';
import { VIRTUAL_BACKGROUND_TYPE } from '../../constants';
import { resizeImage } from '../../functions';
import logger from '../../logger';

/**
 * Component used to upload an image.
 *
 * @param {Object} Props - The props of the component.
 * @returns {React$Node}
 */
function UploadImageButton({
    setLoading,
    setOptions,
    setStoredImages,
    showLabel,
    storedImages,
    t
}) {
    const uploadImageButton = useRef(null);

    const uploadImageKeyPress = useCallback(e => {
        if (uploadImageButton.current && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            uploadImageButton.current.click();
        }
    }, [ uploadImageButton.current ]);

    const uploadImage = useCallback(async e => {
        const imageFile = e.target.files;

        if (imageFile.length === 0) {
            return;
        }

        const reader = new FileReader();

        reader.readAsDataURL(imageFile[0]);
        reader.onload = async () => {
            const url = await resizeImage(reader.result);
            const uuId = uuidv4();

            setStoredImages([
                ...storedImages,
                {
                    id: uuId,
                    src: url
                }
            ]);
            setOptions({
                backgroundEffectEnabled: true,
                backgroundType: VIRTUAL_BACKGROUND_TYPE.IMAGE,
                selectedThumbnail: uuId,
                virtualSource: url
            });
        };

        //  logger.info('New virtual background image uploaded!');

        reader.onerror = () => {
            setLoading(false);
            logger.error('Failed to upload virtual image!');
        };
    }, [ storedImages ]);

    return (
        <>
            {showLabel && <label
                className = 'upload-image-label'
                htmlFor = 'file-upload'
                onKeyPress = { uploadImageKeyPress }
                tabIndex = { 0 }>
                <Icon
                    className = 'add-background-icon'
                    size = { 24 }
                    src = { IconPlus } />
                {t('virtualBackground.addBackground')}
            </label>}

            <input
                accept = 'image/*'
                className = 'upload-input'
                id = 'file-upload'
                onChange = { uploadImage }
                ref = { uploadImageButton }
                role = 'button'
                type = 'file' />
        </>
    );
}

export default translate(UploadImageButton);
